/**
 * SimilarRecordsPanel — inline "similar past records" suggestions under a
 * create form's description field.
 *
 * Collapsed one-line strip by default; expanding shows small cards with
 * "Start from this" (which routes through the SAME mapper/apply path as AI
 * prefill, so the mandatory warning banner fires). Dismissable per page
 * visit (component state only — reappears next visit by design).
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, History, X } from 'lucide-react';

export default function SimilarRecordsPanel({
  suggestions = [],
  loading = false,
  onStartFrom,
  title = 'similar past records',
}) {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || (!loading && suggestions.length === 0)) return null;

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 text-sm">
      <div className="flex items-center justify-between px-3 py-1.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
        >
          <History className="h-4 w-4 text-gray-400" />
          {loading
            ? `Looking for ${title}…`
            : `${suggestions.length} ${title} found`}
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Dismiss suggestions"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && suggestions.length > 0 && (
        <ul className="space-y-2 border-t border-gray-200 p-3">
          {suggestions.map((rec) => (
            <li
              key={rec.id}
              className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-800">
                  {rec.permit_number || rec.assessment_number || rec.capa_number || rec.id}
                  {rec.permit_type ? ` · ${rec.permit_type}` : ''}
                  {rec.title ? ` · ${rec.title}` : ''}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {(rec.work_description || rec.description || '').slice(0, 120)}
                </p>
                <p className="text-xs text-gray-400">
                  {rec.site_name ? `${rec.site_name} · ` : ''}
                  {rec.created_at ? new Date(rec.created_at).toLocaleDateString() : ''}
                  {typeof rec.score === 'number' ? ` · ${Math.round(rec.score * 100)}% match` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onStartFrom?.(rec)}
                className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                Start from this
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
